const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const rmsToDb = (rms, floorDb = -120) => {
  const safe = Number.isFinite(rms) ? Math.max(rms, 1e-8) : 1e-8;
  return Math.max(floorDb, 20 * Math.log10(safe));
};

const createError = (code, message = code) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const positiveChannelCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? Math.min(32, Math.max(1, Math.floor(numeric)))
    : null;
};

const buildRequestedAudioConstraints = ({ channelCount = 2, deviceId = null } = {}) => ({
  ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  channelCount: { ideal: Math.max(1, channelCount) },
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false
});

const createTrackAudioSource = (context, audioTrack) => {
  if (typeof context.createMediaStreamTrackSource === 'function') {
    return context.createMediaStreamTrackSource(audioTrack);
  }

  if (typeof window.MediaStreamTrackAudioSourceNode === 'function') {
    return new window.MediaStreamTrackAudioSourceNode(context, {
      mediaStreamTrack: audioTrack
    });
  }

  return context.createMediaStreamSource(new MediaStream([audioTrack]));
};

const createDiscreteSplitter = (context, channelCount) => {
  let node;

  if (typeof window.ChannelSplitterNode === 'function') {
    try {
      node = new window.ChannelSplitterNode(context, {
        numberOfOutputs: channelCount,
        channelCount,
        channelCountMode: 'explicit',
        channelInterpretation: 'discrete'
      });
    } catch (_) {}
  }

  if (!node) node = context.createChannelSplitter(channelCount);

  try { node.channelCountMode = 'explicit'; } catch (_) {}
  try { node.channelInterpretation = 'discrete'; } catch (_) {}

  return node;
};

const configureMonoAnalyser = (context, fftSize) => {
  const analyser = context.createAnalyser();
  analyser.fftSize = fftSize;
  analyser.smoothingTimeConstant = 0;

  try { analyser.channelCount = 1; } catch (_) {}
  try { analyser.channelCountMode = 'explicit'; } catch (_) {}
  try { analyser.channelInterpretation = 'discrete'; } catch (_) {}

  return analyser;
};

/**
 * Núcleo de medição do tutorial.
 *
 * Recebe qualquer MediaStream com áudio, preserva os canais discretos da fonte
 * e entrega RMS/peak em dBFS. Microfone, interface USB e captura de aba usam o
 * mesmo pipeline.
 *
 * A separação L/R não depende de downmix para "speakers". Cada saída do
 * ChannelSplitter entra em um AnalyserNode explicitamente mono e discreto.
 *
 * `gainDb` continua sendo somente uma referência visual do tutorial. Nenhum
 * valor representa dB SPL calibrado.
 */
const createStreamAudioMeter = ({
  streamFactory,
  sourceKind = 'input',
  floor = -20,
  ceiling = 6,
  gainDb = 18,
  fps = 30,
  attack = 0.48,
  release = 0.12,
  fftSize = 2048,
  channels = 'auto',
  onLevel = null,
  onError = null
} = {}) => {
  let stream = null;
  let context = null;
  let source = null;
  let splitter = null;
  let analysers = [];
  let sampleBuffers = [];
  let frame = null;
  let running = false;
  let smoothedDb = [];
  let lastFrameAt = 0;
  let activeChannelCount = 0;
  let reportedTrackChannelCount = null;

  const configuredChannelCount = positiveChannelCount(channels);

  const stop = async () => {
    running = false;

    if (frame !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(frame);
      frame = null;
    }

    try { source?.disconnect(); } catch (_) {}
    try { splitter?.disconnect(); } catch (_) {}
    analysers.forEach((analyser) => {
      try { analyser?.disconnect(); } catch (_) {}
    });

    stream?.getTracks?.().forEach((track) => track.stop());

    if (context && context.state !== 'closed') {
      try { await context.close(); } catch (_) {}
    }

    stream = null;
    context = null;
    source = null;
    splitter = null;
    analysers = [];
    sampleBuffers = [];
    smoothedDb = [];
    lastFrameAt = 0;
    activeChannelCount = 0;
    reportedTrackChannelCount = null;
  };

  const measureChannel = (analyser, samples, index) => {
    analyser.getFloatTimeDomainData(samples);

    let sumSquares = 0;
    let peak = 0;

    for (let sampleIndex = 0; sampleIndex < samples.length; sampleIndex += 1) {
      const sample = samples[sampleIndex];
      sumSquares += sample * sample;
      peak = Math.max(peak, Math.abs(sample));
    }

    const rms = Math.sqrt(sumSquares / samples.length);
    const dbfs = rmsToDb(rms);
    const peakDbfs = rmsToDb(peak);
    const targetDb = clamp(dbfs + gainDb, floor, ceiling);
    const previous = Number.isFinite(smoothedDb[index]) ? smoothedDb[index] : floor;
    const smoothing = targetDb > previous ? attack : release;
    const db = clamp(previous + ((targetDb - previous) * clamp(smoothing, 0, 1)), floor, ceiling);
    const peakDb = clamp(peakDbfs + gainDb, floor, ceiling);

    smoothedDb[index] = db;

    return {
      index,
      db,
      peakDb,
      dbfs,
      peakDbfs,
      rms,
      peak
    };
  };

  const readLevel = (timestamp = 0) => {
    if (!running || !analysers.length) return;

    const minFrameMs = 1000 / Math.max(1, fps);
    if (timestamp - lastFrameAt >= minFrameMs) {
      lastFrameAt = timestamp;

      const outputChannels = analysers.map((analyser, index) => (
        measureChannel(analyser, sampleBuffers[index], index)
      ));

      if (typeof onLevel === 'function') {
        const db = outputChannels.reduce((sum, item) => sum + item.db, 0) / outputChannels.length;
        const peakDb = Math.max(...outputChannels.map((item) => item.peakDb));
        const dbfs = outputChannels.reduce((sum, item) => sum + item.dbfs, 0) / outputChannels.length;
        const peakDbfs = Math.max(...outputChannels.map((item) => item.peakDbfs));

        onLevel({
          sourceKind,
          channelCount: outputChannels.length,
          reportedTrackChannelCount,
          channels: outputChannels,
          db,
          peakDb,
          dbfs,
          peakDbfs,
          rms: outputChannels[0]?.rms ?? 0,
          peak: outputChannels[0]?.peak ?? 0
        });
      }
    }

    frame = requestAnimationFrame(readLevel);
  };

  const start = async () => {
    if (running) return;

    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      const error = createError('AUDIO_CAPTURE_NOT_SUPPORTED');
      if (typeof onError === 'function') onError(error);
      throw error;
    }

    if (window.isSecureContext === false) {
      const error = createError('AUDIO_CAPTURE_REQUIRES_SECURE_CONTEXT');
      if (typeof onError === 'function') onError(error);
      throw error;
    }

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      const error = createError('WEB_AUDIO_NOT_SUPPORTED');
      if (typeof onError === 'function') onError(error);
      throw error;
    }

    if (typeof streamFactory !== 'function') {
      const error = createError('AUDIO_SOURCE_NOT_CONFIGURED');
      if (typeof onError === 'function') onError(error);
      throw error;
    }

    try {
      context = new AudioContextCtor();
      if (context.state === 'suspended') {
        await context.resume();
      }

      stream = await streamFactory();
      const audioTracks = stream?.getAudioTracks?.() || [];
      if (!audioTracks.length) {
        throw createError(
          sourceKind === 'display' ? 'DISPLAY_AUDIO_NOT_AVAILABLE' : 'AUDIO_INPUT_NOT_AVAILABLE'
        );
      }

      const audioTrack = audioTracks[0];
      reportedTrackChannelCount = positiveChannelCount(
        audioTrack?.getSettings?.()?.channelCount
      );

      const minimumChannels = sourceKind === 'display' ? 2 : 1;
      const requestedChannels = configuredChannelCount || Math.max(
        minimumChannels,
        reportedTrackChannelCount || 0
      );

      // Se a origem aceitar constraints de canal/processamento, pedimos o modo
      // mais neutro possível. Falha aqui não invalida a captura; alguns tipos de
      // display track simplesmente ignoram essas constraints.
      if (typeof audioTrack.applyConstraints === 'function') {
        try {
          await audioTrack.applyConstraints(buildRequestedAudioConstraints({
            channelCount: requestedChannels
          }));
        } catch (_) {}
      }

      reportedTrackChannelCount = positiveChannelCount(
        audioTrack?.getSettings?.()?.channelCount
      ) || reportedTrackChannelCount;

      activeChannelCount = configuredChannelCount || Math.max(
        minimumChannels,
        reportedTrackChannelCount || 0
      );
      activeChannelCount = Math.min(32, Math.max(1, activeChannelCount));

      // Usa explicitamente o track de áudio, em vez de entregar o MediaStream
      // inteiro ao Web Audio e depender da seleção implícita do primeiro track.
      source = createTrackAudioSource(context, audioTrack);
      splitter = createDiscreteSplitter(context, activeChannelCount);
      source.connect(splitter);

      analysers = Array.from({ length: activeChannelCount }, (_, index) => {
        const analyser = configureMonoAnalyser(context, fftSize);
        splitter.connect(analyser, index, 0);
        return analyser;
      });

      sampleBuffers = analysers.map((analyser) => new Float32Array(analyser.fftSize));
      smoothedDb = analysers.map(() => floor);

      // Nada é conectado ao destination. O áudio é somente medido, evitando
      // eco/realimentação com microfone e duplicação de áudio ao capturar aba.
      lastFrameAt = 0;
      running = true;
      frame = requestAnimationFrame(readLevel);
    } catch (error) {
      await stop();
      if (typeof onError === 'function') onError(error);
      throw error;
    }
  };

  return {
    start,
    stop,
    get running() {
      return running;
    },
    get channelCount() {
      return activeChannelCount;
    },
    get reportedChannelCount() {
      return reportedTrackChannelCount;
    }
  };
};

/**
 * Entrada física padrão. `deviceId` permite apontar para uma interface de áudio
 * USB; nesse caso guitarra, baixo ou teclado ligados à interface aparecem como
 * canais independentes quando o dispositivo os expõe ao navegador.
 */
export const createMicrophoneAudioMeter = ({
  deviceId = null,
  constraints = null,
  ...options
} = {}) => createStreamAudioMeter({
  ...options,
  sourceKind: 'input',
  streamFactory: async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw createError('MICROPHONE_NOT_SUPPORTED');
    }

    const requested = positiveChannelCount(options.channels) || 2;
    const audio = constraints || buildRequestedAudioConstraints({
      channelCount: requested,
      deviceId
    });

    return navigator.mediaDevices.getUserMedia({ audio });
  }
});

/**
 * Captura áudio de uma aba/janela/tela escolhida pelo usuário. A solicitação já
 * pede dois canais e desativa processamento de voz para evitar downmix mono.
 */
export const createDisplayAudioMeter = (options = {}) => createStreamAudioMeter({
  ...options,
  sourceKind: 'display',
  streamFactory: async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw createError('DISPLAY_CAPTURE_NOT_SUPPORTED');
    }

    const requested = positiveChannelCount(options.channels) || 2;

    return navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: {
        channelCount: { ideal: requested },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        suppressLocalAudioPlayback: false
      }
    });
  }
});

export const listAudioInputs = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === 'audioinput')
    .map((device) => ({
      deviceId: device.deviceId,
      groupId: device.groupId,
      label: device.label || 'Entrada de áudio'
    }));
};

export default createMicrophoneAudioMeter;
